import {ChangeDetectionStrategy, Component, computed, inject, signal} from "@angular/core"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {
    ImportTextureSetDialogSourceGQL,
    ImportTextureSetGQL,
    ImportTextureSetRevisionFragment,
    ImportTextureSetSourceFragment,
} from "@app/textures/import-texture-set-dialog/import-texture-set-dialog.generated"
import {getRevisionTitle} from "@app/textures/utils/texture-set-revision-utils"
import {getSimpleJobState, SimpleJobState} from "@app/textures/utils/simple-job-state"
import {descriptorByTextureType, textureTypes} from "@app/textures/utils/texture-type-descriptor"
import {CardComponent} from "@common/components/cards"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {TextureType} from "@generated"

// Either imports the chosen revision into a target material, or hands back one of its maps to the caller.
export type ImportTextureSetDialogData = {
    sourceMaterial: {id: string; legacyId: number; name?: string | null}
} & ({targetMaterialId: string} | {selectTextureMap: {defaultTextureType: TextureType}})

export type ImportTextureSetDialogResult =
    | {outcome: "back"}
    | {outcome: "imported"; textureGroupId: string}
    | {outcome: "selected"; textureSetRevisionId: string; textureType: TextureType}

type RevisionOption = {
    revision: ImportTextureSetRevisionFragment
    title: string
    thumbnailId: string | undefined
    blockedReason: string | undefined
}

const getThumbnailId = (revision: ImportTextureSetRevisionFragment | undefined) =>
    (revision?.mapAssignments.find((mapAssignment) => mapAssignment.textureType === TextureType.Diffuse) ?? revision?.mapAssignments[0])?.dataObject.id

// until its bake has completed a revision holds unbaked maps, which a copy would keep forever (the backend rejects these too)
// and which don't show what the revision is supposed to look like when selecting one of its maps
const getBlockedReason = (revision: ImportTextureSetRevisionFragment): string | undefined => {
    const job = revision.editsProcessingJob
    if (!job) {
        return undefined
    }
    switch (getSimpleJobState(job.state)) {
        case SimpleJobState.InProgress:
            return "Edits are still being processed"
        case SimpleJobState.Failed:
            return "Processing the edits failed"
        case SimpleJobState.Cancelled:
            return "Processing the edits was cancelled"
        case SimpleJobState.Complete:
            return undefined
    }
}

// revisions are sorted newest first, so the version is counted from the end of the full list, matching the texture editor
const getRevisionOptions = (textureSet: ImportTextureSetSourceFragment): RevisionOption[] =>
    textureSet.textureSetRevisions
        .map((revision, index, revisions) => ({
            revision,
            title: getRevisionTitle(revision, revisions.length - index),
            thumbnailId: getThumbnailId(revision),
            blockedReason: getBlockedReason(revision),
        }))
        .filter(({revision}) => revision.mapAssignments.length > 0)

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-import-texture-set-dialog",
    templateUrl: "./import-texture-set-dialog.component.html",
    styleUrl: "./import-texture-set-dialog.component.scss",
    imports: [MatDialogModule, MatButtonModule, CardComponent, DataObjectThumbnailComponent, TimeAgoPipe],
})
export class ImportTextureSetDialogComponent {
    private readonly dialogRef = inject<MatDialogRef<ImportTextureSetDialogComponent, ImportTextureSetDialogResult>>(MatDialogRef)
    private readonly notifications = inject(NotificationsService)
    private readonly fetchSource = fetchThrowingErrors(inject(ImportTextureSetDialogSourceGQL))
    private readonly importTextureSet = mutateThrowingErrors(inject(ImportTextureSetGQL))

    protected readonly data = inject<ImportTextureSetDialogData>(MAT_DIALOG_DATA)
    protected readonly selectTextureMap = "selectTextureMap" in this.data ? this.data.selectTextureMap : undefined
    protected readonly getThumbnailId = getThumbnailId

    // undefined while loading
    protected readonly $textureSets = signal<ImportTextureSetSourceFragment[] | undefined>(undefined)
    private readonly $chosenTextureSetId = signal<string | undefined>(undefined)
    private readonly $chosenRevisionId = signal<string | undefined>(undefined)
    private readonly $chosenTextureType = signal<TextureType | undefined>(undefined)
    protected readonly $importing = signal(false)

    protected readonly $textureSet = computed(() => {
        const textureSets = this.$textureSets()
        return textureSets?.length === 1 ? textureSets[0] : textureSets?.find(({id}) => id === this.$chosenTextureSetId())
    })

    protected readonly $revisionOptions = computed(() => {
        const textureSet = this.$textureSet()
        return textureSet ? getRevisionOptions(textureSet) : []
    })

    protected readonly $revisionOption = computed(() => {
        const options = this.$revisionOptions()
        if (options.length === 1 && !options[0].blockedReason) {
            return options[0]
        }
        return options.find(({revision}) => revision.id === this.$chosenRevisionId())
    })

    protected readonly $textureMapOptions = computed(() => {
        const mapAssignments = this.$revisionOption()?.revision.mapAssignments ?? []
        return textureTypes.flatMap((textureType) => {
            const mapAssignment = mapAssignments.find((assignment) => assignment.textureType === textureType)
            return mapAssignment ? [{textureType, label: descriptorByTextureType(textureType).label, dataObjectId: mapAssignment.dataObject.id}] : []
        })
    })

    // undefined until there is a choice, which is the case if the revision doesn't have the default type
    protected readonly $textureMapOption = computed(() => {
        const textureType = this.$chosenTextureType() ?? this.selectTextureMap?.defaultTextureType
        return this.$textureMapOptions().find((option) => option.textureType === textureType)
    })

    protected readonly $confirmThumbnailId = computed(() => this.$textureMapOption()?.dataObjectId ?? this.$revisionOption()?.thumbnailId)

    protected readonly $step = computed(() => {
        const textureSets = this.$textureSets()
        if (!textureSets) {
            return "loading"
        }
        if (textureSets.length === 0) {
            return "empty"
        }
        if (!this.$textureSet()) {
            return "textureSet"
        }
        return this.$revisionOption() ? "confirm" : "revision"
    })

    constructor() {
        void this.loadTextureSets()
    }

    private async loadTextureSets() {
        try {
            const {material} = await this.fetchSource({materialId: this.data.sourceMaterial.id})
            this.$textureSets.set((material.textureGroup?.textureSets ?? []).filter((textureSet) => getRevisionOptions(textureSet).length > 0))
        } catch (error) {
            this.notifications.showError(error)
            this.dialogRef.close()
        }
    }

    protected chooseTextureSet(textureSet: ImportTextureSetSourceFragment) {
        this.$chosenTextureSetId.set(textureSet.id)
    }

    protected chooseRevision(option: RevisionOption) {
        this.$chosenRevisionId.set(option.revision.id)
    }

    protected chooseTextureType(textureType: TextureType) {
        this.$chosenTextureType.set(textureType)
    }

    // returns to the previous screen that actually offered a choice, and from the first one to the material selection
    protected back() {
        const step = this.$step()
        this.$chosenTextureType.set(undefined)
        if (step === "confirm" && this.$revisionOptions().length > 1) {
            this.$chosenRevisionId.set(undefined)
        } else if ((step === "confirm" || step === "revision") && (this.$textureSets()?.length ?? 0) > 1) {
            this.$chosenRevisionId.set(undefined)
            this.$chosenTextureSetId.set(undefined)
        } else {
            this.dialogRef.close({outcome: "back"})
        }
    }

    protected cancel() {
        this.dialogRef.close()
    }

    protected confirmSelection() {
        const revision = this.$revisionOption()?.revision
        const textureMapOption = this.$textureMapOption()
        if (revision && textureMapOption) {
            this.dialogRef.close({outcome: "selected", textureSetRevisionId: revision.id, textureType: textureMapOption.textureType})
        }
    }

    protected async confirmImport() {
        const revision = this.$revisionOption()?.revision
        if (!revision || !("targetMaterialId" in this.data)) {
            return
        }
        const {targetMaterialId} = this.data
        this.$importing.set(true)
        const result = await this.notifications.withUserFeedback(
            () => this.importTextureSet({input: {targetMaterialId, sourceTextureSetRevisionId: revision.id}}),
            {success: "Texture set imported.", error: (message) => `Cannot import texture set: ${message}`},
        )
        this.$importing.set(false)
        if (result) {
            this.dialogRef.close({outcome: "imported", textureGroupId: result.importTextureSet.textureGroup.id})
        }
    }
}
